import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/shared";
import { GoogleButton, PinInput } from "@/components/GoogleButton";
import { api, errMsg, saveToken } from "@/lib/api";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const inputCls = "w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 outline-none rounded-md px-3 py-2.5";

export default function Activate() {
  const { user, loading, setUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ name: "", username: "", pin: "", pin2: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (code.length !== 6) { setPreview(null); return; }
    let live = true;
    api.get(`/auth/invite/${code}`).then((r) => live && (setPreview(r.data), setErr(""), setForm((f) => ({ ...f, name: f.name || r.data.name || "" }))))
      .catch((e) => live && (setPreview(null), setErr(errMsg(e))));
    return () => { live = false; };
  }, [code]);

  if (loading) return <div className="min-h-screen mjm-bg"><Spinner /></div>;
  if (user && user.role !== "PENDING") return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (form.pin !== form.pin2) return setErr("Konfirmasi PIN tidak sama");
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/activate", { code, name: form.name, username: form.username, pin: form.pin });
      saveToken(data);
      setUser(data);
      navigate("/app", { replace: true });
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen mjm-bg mjm-grain flex items-center justify-center p-6">
      <div className="w-full max-w-md panel-red p-8 fade-up" data-testid="activate-page">
        <img src="/logo.png" alt="MJM Workshop" className="h-14 mx-auto mb-6" />
        <div className="eyebrow">Aktivasi akun staf</div>
        <h1 className="font-display text-3xl font-bold mt-1 mb-5">Masukkan kode undangan</h1>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 uppercase tracking-wider">Kode aktivasi (6 karakter)</label>
            <input data-testid="activate-code-input" value={code} maxLength={6} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              placeholder="XXXXXX" className={`${inputCls} font-mono tracking-[0.5em] text-xl text-center mt-1`} autoFocus />
          </div>
          {preview && (
            <div className="panel p-3 flex items-start gap-3 text-sm" data-testid="activate-preview">
              <ShieldCheck className="h-5 w-5 text-emerald-400 mt-0.5" />
              <div>Undangan untuk <span className="font-semibold text-white">{preview.name || "staf"}</span> sebagai <span className="font-mono text-red-400">{preview.role}</span>{preview.email && <div className="text-xs text-zinc-500 font-mono">{preview.email}</div>}</div>
            </div>
          )}
          {preview && (<>
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider">Nama lengkap</label>
              <input data-testid="activate-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} mt-1`} required />
            </div>
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider">Username (untuk login PIN)</label>
              <input data-testid="activate-username-input" value={form.username} autoCapitalize="none" onChange={(e) => setForm({ ...form, username: e.target.value.replace(/[^A-Za-z0-9._ ]/g, "") })}
                placeholder="mis. Budi Santoso" className={`${inputCls} font-mono mt-1`} required minLength={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-zinc-400 uppercase tracking-wider">PIN / Password</label><div className="mt-1"><PinInput value={form.pin} onChange={(v) => setForm({ ...form, pin: v })} testId="activate-pin-input" placeholder="Password" /></div></div>
              <div><label className="text-xs text-zinc-400 uppercase tracking-wider">Ulangi PIN</label><div className="mt-1"><PinInput value={form.pin2} onChange={(v) => setForm({ ...form, pin2: v })} testId="activate-pin2-input" placeholder="Ulangi" /></div></div>
            </div>
          </>)}
          {err && <div className="text-sm text-red-300 bg-red-950/60 border border-red-900 rounded p-2" data-testid="activate-error">{err}</div>}
          {preview && (
            <button type="submit" disabled={busy || form.pin.length < 4 || !form.username} data-testid="activate-submit"
              className="w-full bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-md py-3 transition-colors">{busy ? "Mengaktifkan…" : "Aktifkan & masuk"}</button>
          )}
        </form>

        {preview?.email && (
          <div className="mt-5 pt-4 border-t border-zinc-800/80 space-y-2">
            <p className="text-[11px] text-zinc-500 text-center">Atau masuk dengan Google <span className="font-mono text-zinc-400">{preview.email}</span> — akun langsung aktif.</p>
            <GoogleButton label="Aktivasi dengan Google" testId="activate-google-btn" />
          </div>
        )}
        <Link to="/login" data-testid="activate-back-login" className="mt-5 flex items-center justify-center gap-1 text-xs text-zinc-500 hover:text-white"><ArrowLeft className="h-3 w-3" /> Kembali ke halaman masuk</Link>
      </div>
    </div>
  );
}
